import React from "react";
import ReactDOM from "react-dom/client";
import "./App.css";
import "./roam-motion.css";
import { initErrorReporter, reportError } from "./errorReporter";

initErrorReporter();

// ─────────────────────────────────────────────────────────────────────────
// Runtime Supabase config
// ─────────────────────────────────────────────────────────────────────────
// REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY get baked into the JS
// bundle at BUILD time by Create React App. That works fine when Netlify
// runs the build itself, but is a dead end for a drag-and-drop zip deploy —
// there is no build step to pick up whatever is set in Netlify's env var
// dashboard, so editing it there has no effect until someone rebuilds and
// re-zips locally.
//
// To fix that, we fetch the same values from a tiny Netlify Function
// (netlify/functions/supabase-config.js) that reads them live on every
// request. `import("./App")` is a dynamic import, so the App module tree —
// and AuthModal.js, which creates the Supabase client at module load time —
// isn't evaluated until this fetch has resolved and window.__SUPABASE_CONFIG__
// is populated. AuthModal.js checks this runtime config first, so rotating
// the key in Netlify's dashboard takes effect immediately — a normal
// `npm start` / `netlify build` with a .env file still works too, since
// REACT_APP_* is the fallback if this fetch fails (e.g. offline).
async function loadSupabaseConfig() {
  // Try the live config (so key rotation still works), with a timeout and one
  // retry. If the network is slow or fails, fall back to the last config that
  // worked on this device so the app still starts instead of hanging.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 3500);
      const res = await fetch("/.netlify/functions/supabase-config", { signal: ctrl.signal });
      clearTimeout(t);
      if (res.ok) {
        const cfg = await res.json();
        window.__SUPABASE_CONFIG__ = cfg;
        try { localStorage.setItem("roam-sb-config", JSON.stringify(cfg)); } catch (e) {}
        return;
      }
    } catch (e) {
      // timeout / offline — retry, then fall back below
    }
  }
  try {
    const cached = JSON.parse(localStorage.getItem("roam-sb-config") || "null");
    if (cached && typeof cached === "object") { window.__SUPABASE_CONFIG__ = cached; return; }
  } catch (e) {}
  window.__SUPABASE_CONFIG__ = {};
}

// Loading the app bundle can fail on flaky mobile networks. Retry quietly.
async function importAppWithRetry() {
  let lastErr;
  for (let i = 0; i < 3; i++) {
    try { return await import("./App"); }
    catch (e) { lastErr = e; await new Promise((r) => setTimeout(r, 800 * (i + 1))); }
  }
  throw lastErr;
}

async function bootstrap() {
  await loadSupabaseConfig();

  const { AppWithErrorBoundary } = await importAppWithRetry();

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <AppWithErrorBoundary />
    </React.StrictMode>
  );

  // React has mounted — dismiss the static offline/loading shell from index.html.
  // A tiny delay avoids a visible flash if the app's own first paint isn't ready yet.
  const shell = document.getElementById("roam-offline-shell");
  if (shell) {
    shell.dataset.dismissed = "1";
    requestAnimationFrame(() => {
      shell.style.transition = "opacity 0.25s ease";
      shell.style.opacity = "0";
      setTimeout(() => shell.remove(), 260);
    });
  }
}

bootstrap().catch((err) => {
  // Log for developers only — users never see technical errors.
  console.error("Roam failed to start:", err);
  reportError(err, "startup");
  // Recover automatically: reload once (clears a stale/partial load).
  try {
    if (!sessionStorage.getItem("roam-boot-retried")) {
      sessionStorage.setItem("roam-boot-retried", "1");
      window.location.reload();
      return;
    }
  } catch (e) {}
  // Still failing: friendly message + retry button, no technical text.
  const msg = document.getElementById("roam-offline-msg");
  const btn = document.getElementById("roam-offline-retry");
  if (msg) msg.textContent = "Roam is having trouble connecting. Please try again.";
  if (btn) btn.style.display = "inline-block";
});
